import { useEffect, useState, useRef, useMemo } from 'react';
import { useDataContext } from './Contexts/DataContext';
import { ComboBox, Item, Key } from '@adobe/react-spectrum';
import { DataComponentProps } from './DataComponentProps';
import { useTranslation } from 'react-i18next';
import { usePreloadAssets } from '../../../hooks/UsePreloadAssets';

const TrackingCategoryDropDown: React.FC<DataComponentProps> = ({ compKey, label, width, isRequired, value: dvalue, onValueChange, dimension }) => {
  const { t } = useTranslation();
  const _t = (key: string) => t(`newContextDialog.dataComponents.${key}`, { ns: 'finder' });
  const messageRef = useRef(_t('is_required').split('[content]').join(label));
  const [errorMessage, setErrorMessage] = useState(isRequired ? messageRef.current : '');
  const [value, setValue] = useState<Key | null>('');
  const [input, setInput] = useState('');
  const [headerLabel, setHeaderLabel] = useState('');
  const { getTrackingCategoryData } = useDataContext();
  const { dimensionSettings } = usePreloadAssets();

  const apiOptions = useMemo(() => ({
    rootAndChildren: '1',
    dimension: dimension
  }), [dimension]);
  
  const { data, error, loading } = getTrackingCategoryData(apiOptions);

  useEffect(() => {
    if(dvalue)
      setInput(dvalue);
  }, [dvalue]);

  useEffect(() => {
    var dim_name = "";
    switch(dimension) {
      case 1:
        dim_name = dimensionSettings.dimension1name;
        break;
      case 2:
        dim_name = dimensionSettings.dimension2name;
        break;
      case 3:
        dim_name = dimensionSettings.dimension3name;
        break;
      default:
        dim_name = '';
        break;
    }
    setHeaderLabel(dim_name);
    messageRef.current = _t('is_required').split('[content]').join(dim_name);
    setErrorMessage(isRequired ? messageRef.current : '');
  }, [dimension, dimensionSettings]);

  const onSelectionChange = (value: Key | null) => {
    if(onValueChange)
        onValueChange(value ? value.toString() : '', input);
    setValue(value);
    if (value) {
      const selectedItem = data.find(item => item.id === value);
      if (selectedItem) {
        setInput(selectedItem.name);
      }
    }
    if(isRequired && !value) 
      setErrorMessage(messageRef.current);
    else 
      setErrorMessage('');
  };

  const onInputChange = (value: string) => {
    setInput(value);
  }

  if (loading) {
    return <div>Loading...</div>;
  }
  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <ComboBox key={compKey} 
        label={headerLabel || label || 'Tracking Category'} 
        width={width} 
        isRequired={isRequired} 
        selectedKey={value}
        onSelectionChange={onSelectionChange} 
        onInputChange={onInputChange}
        validationState={errorMessage ? 'invalid' : undefined}
        errorMessage={errorMessage}
        inputValue={input}
        >
      {data.map((item) => (
        <Item key={item.id}>
          {item.name}
        </Item>
      ))}
    </ComboBox>
  );
};

export default TrackingCategoryDropDown;
